'use client';

import { useEffect, useRef } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import { Splitter } from '@terra/ui-design-system';
import { useTerminalTabs } from '../../../store/terminalStore';
import { useBottomPanelStore } from '../../../store/bottomPanelStore';
import { TerminalSidebar } from './TerminalSidebar';
import { Terminal } from '../../Terminal';

export function TerminalContent() {
  const {
    terminalTabs,
    activeTerminalTab,
    addTerminalTab,
    removeTerminalTab,
    setActiveTerminalTab,
    defaultShell,
  } = useTerminalTabs();

  const isTerminalPanelVisible = useBottomPanelStore(
    (state) => state.isOpen && state.selectedTab === 'terminal',
  );

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const initTerminals = async () => {
      if (!window.electronAPI?.terminal) return;

      if (terminalTabs.length === 0) {
        const sessionId = `terminal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        addTerminalTab({
          id: sessionId,
          title: defaultShell,
          sessionId,
          shell: defaultShell,
        });
      }
    };

    initTerminals();
  }, [terminalTabs, addTerminalTab, defaultShell]);

  if (terminalTabs.length === 0) {
    return null;
  }

  return (
    <VStack h="full" w="full" gap={0}>
      <Splitter.Root
        orientation="horizontal"
        flex="1"
        w="full"
        panels={[
          { id: 'terminal-main', minSize: 50 },
          { id: 'terminal-sidebar', minSize: 10, maxSize: 30 },
        ]}
        defaultSize={[92, 8]}
        borderWidth="0"
        gap={0}
      >
        <Splitter.Panel
          id="terminal-main"
          borderWidth="0"
          borderRadius="none"
          alignItems="stretch"
          justifyContent="flex-start"
        >
          <Box h="full" w="full" overflow="hidden" position="relative">
            {terminalTabs.map((tab) => (
              <Box
                key={tab.instanceKey ?? tab.sessionId}
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                visibility={tab.isActive ? 'visible' : 'hidden'}
                zIndex={tab.isActive ? 1 : 0}
                pointerEvents={tab.isActive ? 'auto' : 'none'}
              >
                <Terminal
                  sessionId={tab.sessionId}
                  ptyId={tab.ptyId}
                  cwd={tab.cwd}
                  shell={tab.shell}
                  isVisible={tab.isActive && isTerminalPanelVisible}
                />
              </Box>
            ))}
          </Box>
        </Splitter.Panel>

        <Splitter.ResizeTrigger
          id="terminal-main:terminal-sidebar"
          width="0px"
          minWidth="0px"
          bg="transparent"
          position="relative"
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: '-2px',
            width: '5px',
            cursor: 'ew-resize',
            zIndex: 10,
          }}
          _hover={{
            _before: {
              bg: 'colorPalette.emphasized',
            },
          }}
        />

        <Splitter.Panel
          id="terminal-sidebar"
          borderWidth="0"
          borderRadius="none"
          borderLeftWidth="1px"
          borderColor="border.subtle"
          alignItems="stretch"
          justifyContent="flex-start"
        >
          <TerminalSidebar
            terminalTabs={terminalTabs}
            activeTerminalTab={activeTerminalTab}
            setActiveTerminalTab={setActiveTerminalTab}
            removeTerminalTab={removeTerminalTab}
          />
        </Splitter.Panel>
      </Splitter.Root>
    </VStack>
  );
}
